import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import moment from 'moment';
import {UsersInsuranceService} from '../../services/usersinsurance/users.insurance.service';
import {UsersService} from '../../services/users/users.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {SnackBarComponent} from '../snackbar/snackbar.component';
import {buildTobaccoUseInput, TobaccoNonUseType} from '../../services/usersinsurance/tobacco-use.model';

@Component({
    selector: 'app-insurance-primary',
    templateUrl: './insurance-primary.component.html',
    styleUrls: ['./insurance-primary.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InsurancePrimaryComponent implements OnInit {

  public form;
  public maxDate = moment().toDate();
  private primaryInfo: any;

  constructor(
      private formBuilder: UntypedFormBuilder,
      private usersInsuranceService: UsersInsuranceService,
      private usersService: UsersService,
      private snackBar: MatSnackBar
  ) {
    this.form = this.formBuilder.group({
      gender: new UntypedFormControl(''),
      usesTobacco: new UntypedFormControl(null),
      tobaccoNonUseType: new UntypedFormControl(null),
      tobaccoQuitDate: new UntypedFormControl('')
    });
    this.form.valueChanges.subscribe(() => this.updateTobaccoUseValidators());
  }

  ngOnInit(): void {
    this.usersInsuranceService.getInsurancePrimary(this.usersService.getCurrentUserId()).then(result => {
      if (result.id) {
        this.primaryInfo = result;
        this.resetClicked();
      }
    });
  }

  resetClicked(): void {
    this.form.reset();
    this.form.get('gender')?.setValue(this.primaryInfo?.gender);
    const nonUseType = this.primaryInfo?.tobaccoUse?.nonUseType;
    this.form.get('usesTobacco')?.setValue(this.primaryInfo?.tobaccoUse?.usesTobacco ?? null);
    this.form.get('tobaccoNonUseType')?.setValue(
      nonUseType === 'FORMER' || nonUseType === 'NEVER' ? nonUseType : null
    );
    this.form.get('tobaccoQuitDate')?.setValue(this.primaryInfo?.tobaccoUse?.quitDate ?? '');
    this.updateTobaccoUseValidators();
  }

  saveClicked(): void {
    const newInfo = {
      gender: this.form.get('gender')?.value,
      tobaccoUse: buildTobaccoUseInput(
        this.form.get('usesTobacco')?.value,
        this.form.get('tobaccoNonUseType')?.value as TobaccoNonUseType | null,
        this.normalizeDate(this.form.get('tobaccoQuitDate')?.value)
      )
    };
    if (this.primaryInfo) {
      this.usersInsuranceService.modifyInsurancePrimary(this.usersService.getCurrentUserId(), newInfo).then(_ => {
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'User insurance primary info updated'});
        this.form.markAsPristine();
      });
    } else {
      this.usersInsuranceService.createInsurancePrimary(
        this.usersService.getCurrentUserId(),
        this.form.get('gender')?.value,
        {},
        newInfo.tobaccoUse
      ).then(_ => {
        this.primaryInfo = newInfo;
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'User insurance primary info created'});
        this.form.markAsPristine();
      });
    }
  }

  private updateTobaccoUseValidators(): void {
    const usesTobacco = this.form.get('usesTobacco')?.value;
    const nonUseType = this.form.get('tobaccoNonUseType');
    const quitDate = this.form.get('tobaccoQuitDate');
    const isFormer = usesTobacco === false && nonUseType?.value === 'FORMER';

    usesTobacco === false ? nonUseType?.setValidators([Validators.required]) : nonUseType?.clearValidators();
    isFormer ? quitDate?.setValidators([Validators.required]) : quitDate?.clearValidators();
    nonUseType?.updateValueAndValidity({emitEvent: false});
    quitDate?.updateValueAndValidity({emitEvent: false});
  }

  private normalizeDate(value: any): string | null {
    if (!value) {
      return null;
    }
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD') : null;
  }

}
